import { useEffect, useState } from 'react';
import { ExerciseAction, IExercise } from '../../../core/models/exercise';
import {
  updateExerciseSelectedAlternative,
  verifyExerciseAnswer,
} from '../../../core/queries/course';
import { useCoursesStore } from '../stores/courseStore';

interface CourseExerciseHook {
  setContentAsDone: () => Promise<void>;
}

export const useCourseExerciseHook = ({
  setContentAsDone,
}: CourseExerciseHook) => {
  const [isLoading, setLoading] = useState(false);

  const { loadedCourse, setCourseExercises } = useCoursesStore();

  const [isRight, setIsRight] = useState<boolean>(false);

  const courseContent = loadedCourse.selectedContentObject;
  const answers = courseContent.answers;
  const statuses = courseContent.statuses;
  const courseExercises = courseContent.targets;

  const organizeExercisesFields = (exercises: IExercise[]) => {
    const organizedExercises = exercises.map((exercise) => {
      if (exercise.alternatives) {
        exercise.alternatives = exercise.alternatives.map((alternative) => {
          alternative.id = alternative._id;

          if (answers[exercise.id]) {
            if (answers[exercise.id].includes(alternative.id)) {
              exercise.hasAlternativeSelected = true;

              if (statuses[exercise.id]) {
                alternative.status =
                  statuses[exercise.id] === 'correct' ? 'success' : 'error';
              }

              alternative.selected = true;
            }
          }
          return alternative;
        });
      }

      exercise.exerciseType = exercise.exercise_type;

      if (statuses[exercise.id]) {
        exercise.done = true;
        exercise.correct = statuses[exercise.id] === 'correct';
        // Snapshot no load: a questão já veio respondida do backend. Diferencia
        // "respondido antes" de "respondido agora" (o done também é setado ao
        // responder na sessão atual, mas previouslyAnswered não).
        exercise.previouslyAnswered = true;
      }

      return exercise;
    });

    return organizedExercises;
  };

  useEffect(() => {
    if (!courseExercises || courseExercises.length === 0) return;

    // Já inicializado para este conteúdo (algum exercício marcado como selected):
    // evita resetar a navegação a cada atualização da lista (responder, trocar
    // de página, etc.).
    if (courseExercises.some((exercise: IExercise) => exercise.selected))
      return;

    const initializedExercises = organizeExercisesFields(courseExercises);
    initializedExercises[0].selected = true;
    setCourseExercises(initializedExercises);
    // Reinicializa quando o conteúdo carregado muda (entrada via SPA a partir da
    // Minha Área / troca de conteúdo), pois os targets chegam sem o selected.
  }, [courseContent._contentId, courseExercises]);

  useEffect(() => {
    if (isRight) {
      setTimeout(function () {
        setIsRight(false);
      }, 7000);
    }
  }, [isRight]);

  const changeExercise = (page: number) => {
    const updatedExercises = courseExercises.map((exercise: IExercise) => {
      exercise.selected = exercise.id === courseExercises[page].id;
      return exercise;
    });

    setIsRight(false);
    setCourseExercises(updatedExercises);
  };

  const getSelectedAlternatives = (exercise: IExercise) => {
    const selectedAlternativesIds = exercise.alternatives
      .filter((alternative) => alternative.selected)
      .map((alternative) => alternative.id);

    return selectedAlternativesIds;
  };

  const isExerciseListComplete = () => {
    const allDone = courseExercises.every((exercise: any) => exercise.done);

    return allDone;
  };

  const updateExercise = async (
    exercise: IExercise,
    action?: ExerciseAction
  ) => {
    const updatedExercises = [...courseExercises];

    const existingExerciseIndex = updatedExercises.findIndex(
      (stateExercise) => stateExercise.id === exercise.id
    );

    updatedExercises[existingExerciseIndex] = exercise;

    setCourseExercises(updatedExercises);

    if (action) {
      const selectedAlternativesIds = getSelectedAlternatives(exercise);

      if (action === 'selectAlternative') {
        if (selectedAlternativesIds.length > 0) {
          setLoading(true);

          await updateExerciseSelectedAlternative(
            loadedCourse.id,
            loadedCourse.selectedContent,
            exercise.id,
            selectedAlternativesIds
          );

          setLoading(false);
        }
      }

      if (action === 'verifyAnswer') {
        if (selectedAlternativesIds.length > 0) {
          setLoading(true);

          if (exercise.correct) {
            setIsRight(true);
          }

          await verifyExerciseAnswer(
            loadedCourse.id,
            loadedCourse.selectedContent,
            exercise.id,
            selectedAlternativesIds,
            exercise.correct ? 'correct' : 'incorrect'
          );

          if (isExerciseListComplete()) {
            await setContentAsDone();
          }

          setLoading(false);
        }
      }
    }
  };

  return {
    isLoading,
    isRight,
    exercises: courseExercises,
    title: courseContent.value,
    discipline: courseContent.area.slug,
    changeExercise,
    updateExercise,
  };
};
